import PropTypes from "prop-types"
import { useEffect, useState, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button, Flex, Spin } from "antd"
import { CalendarOutlined, LeftOutlined, MoreOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { routes } from "router/routes"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import CustomDrawer from "ui/Drawer"
import IconWithBg from "ui/IconWithBg"
import { DSIcon } from "ui/Icon/DSIcon"
import NotesSection from "../BookingDetailsPage/BookingDetailSummary/NotesSection"
import ReservationSummaryCard from "./components/ReservationSummaryCard/ReservationSummaryCard"
import ClientDetailsCard from "./components/ClientDetailsCard/ClientDetailsCard"
import ExperienceCard from "./components/ExperienceCard/ExperienceCard"
import PaymentsPanel from "./components/PaymentsPanel/PaymentsPanel"
import CommunicationHistoryPanel from "./components/CommunicationHistoryPanel/CommunicationHistoryPanel"
import {
  fetchReservationDetail,
  fetchReservationWaivers,
  fetchReservationPaymentHistory,
  fetchReservationCommunications,
  fetchReservationAnswers
} from "store/reservations"
import CancelBookingModal from "../BookingActions/CancelBookingModal"
import { deriveCheckInState } from "../CheckIn/checkInUtils"
import styles from "./index.module.css"

const ReservationDetailPage = ({ onBack }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id: uuid } = useParams()
  const handleBack = onBack ?? (() => navigate(routes.guideReservationsPath()))
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isActionsOpen, setIsActionsOpen] = useState(false)
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)

  const { data, loading } = useSelector((store) => store.reservations.detail)
  const waiversSlice = useSelector((store) => store.reservations.waivers)
  const paymentHistorySlice = useSelector((store) => store.reservations.paymentHistory)
  const communicationsSlice = useSelector((store) => store.reservations.communications)
  const answersSlice = useSelector((store) => store.reservations.answers)

  const refreshDetail = useCallback(() => {
    if (uuid) dispatch(fetchReservationDetail(uuid))
  }, [uuid, dispatch])

  const refreshWaivers = useCallback(() => {
    if (uuid) dispatch(fetchReservationWaivers(uuid))
  }, [uuid, dispatch])

  // Payment-affecting actions change both the summary and the history list.
  const refreshPayments = useCallback(() => {
    refreshDetail()
    if (uuid) dispatch(fetchReservationPaymentHistory(uuid))
  }, [refreshDetail, uuid, dispatch])

  useEffect(() => {
    refreshDetail()
  }, [refreshDetail])

  // Lazy sub-resources keyed by uuid
  useEffect(() => {
    if (!uuid) return
    dispatch(fetchReservationWaivers(uuid))
    dispatch(fetchReservationPaymentHistory(uuid))
    dispatch(fetchReservationAnswers(uuid))
    dispatch(fetchReservationCommunications(uuid))
  }, [uuid, dispatch])

  if (loading || !data) {
    return (
      <Flex justify="center" align="center" style={{ height: "55vh" }}>
        <Spin size="large" />
      </Flex>
    )
  }

  // Core sections come from the detail payload; waivers/answers/payment-history/
  // communications are lazy-loaded from their own endpoints (slices above).
  const { client, experience, payments } = data

  // Check-in is read-only here — the status is derived for display; check-in itself happens
  // from the reservation/scheduled listings.
  const totalGuests = data.totalGuests ?? 0
  const checkedInCount = data.checkedInCount ?? 0
  const checkInState = deriveCheckInState(checkedInCount, totalGuests, data.checkedOutCount ?? 0)

  return (
    <Flex vertical gap={16} className={styles.page}>
      {/* Breadcrumb */}
      <Flex align="center" gap={4} className={styles.breadcrumb}>
        <IconWithBg icon={<DSIcon name="reservations" width={20} height={20} fill="var(--color-text-primary)" />} onClick={handleBack} />
        <button type="button" className={styles.breadcrumbLink} onClick={handleBack}>
          {t("booking.reservation_detail.breadcrumb_reservations")}
        </button>
        <span className={styles.breadcrumbSep}>·</span>
        <span className={styles.breadcrumbCurrent}>{t("booking.reservation_detail.reservation_number", { id: data.reservationId })}</span>
      </Flex>

      {/* Page title row */}
      <Flex align="flex-start" justify="space-between" className={styles.titleRow}>
        <Flex align="flex-start" gap={8}>
          <IconWithBg className="mt-xxs" icon={<LeftOutlined />} onClick={handleBack} />
          <Flex vertical gap={4}>
            <span className={styles.pageTitle}>{t("booking.reservation_detail.reservation_number", { id: data.reservationId })}</span>
            <Flex align="center" gap={6}>
              <CalendarOutlined className={styles.metaIcon} />
              <span className={styles.metaText}>
                {data.date} | {data.timeStart} - {data.timeEnd}
              </span>
            </Flex>
          </Flex>
        </Flex>
        {!isMobile && (
          <Flex align="center" gap={8}>
            {/* {data.canEdit && (
              <Button variant="outlined" color="danger" className={styles.cancelBtn} onClick={() => setIsCancelModalOpen(true)}>
                {t("booking.reservation_detail.cancel_reservation")}
              </Button>
            )} */}
            {data.canEdit && (
              <Button type="primary" className={styles.editBtn} onClick={() => data.uuid && navigate(routes.guideBookingEditPath({ id: data.uuid }))}>
                {t("booking.reservation_detail.edit_reservation")}
              </Button>
            )}
          </Flex>
        )}
      </Flex>

      {/* Mobile action bar: full-width Edit + kebab that opens the Actions sheet */}
      {isMobile && data.canEdit && (
        <Flex align="center" gap={8} className={styles.mobileActionBar}>
          <Button type="primary" block className={styles.editBtn} onClick={() => data.uuid && navigate(routes.guideBookingEditPath({ id: data.uuid }))}>
            {t("booking.reservation_detail.edit_reservation")}
          </Button>
          <Button type="text" icon={<MoreOutlined />} className={styles.kebabBtn} onClick={() => setIsActionsOpen(true)} />
        </Flex>
      )}

      {/* Body: left main + right sidebar */}
      <Flex gap={16} align="flex-start" className={styles.body}>
        {/* LEFT: main content */}
        <Flex vertical gap={12} className={styles.mainPanel}>
          <Flex gap={12} className={styles.topCards}>
            <ReservationSummaryCard reservation={data} />
            {client && <ClientDetailsCard client={client} bookingUuid={data.uuid} bookingId={data.bookingId} onRefresh={refreshDetail} />}
          </Flex>
          {experience && (
            <ExperienceCard
              experience={experience}
              waivers={waiversSlice.data}
              waiversLoading={waiversSlice.loading}
              answers={answersSlice.data}
              answersLoading={answersSlice.loading}
              bookingUuid={data.uuid}
              onRefresh={refreshWaivers}
              checkInState={checkInState}
              checkedInCount={checkedInCount}
              totalGuests={totalGuests}
            />
          )}
        </Flex>

        {/* RIGHT: sidebar */}
        <Flex vertical gap={12} className={styles.sidePanel}>
          {payments && (
            <PaymentsPanel
              payments={payments}
              reservation={data}
              history={paymentHistorySlice.data}
              historyLoading={paymentHistorySlice.loading}
              onRefresh={refreshPayments}
            />
          )}
          <div className={styles.notesPanel}>
            <NotesSection bookingId={data.uuid} />
          </div>
          <CommunicationHistoryPanel communications={communicationsSlice.data} loading={communicationsSlice.loading} />
        </Flex>
      </Flex>

      {/* Actions bottom sheet (mobile) */}
      {isMobile && data.canEdit && (
        <CustomDrawer open={isActionsOpen} setOpen={setIsActionsOpen} title={t("booking.reservation_detail.actions")}>
          <Flex vertical gap={12} className={styles.actionsSheet}>
            <Button
              block
              shape="round"
              variant="outlined"
              color="danger"
              onClick={() => {
                setIsActionsOpen(false)
                setIsCancelModalOpen(true)
              }}
            >
              {t("booking.reservation_detail.cancel_reservation")}
            </Button>
          </Flex>
        </CustomDrawer>
      )}

      {isCancelModalOpen && (
        <CancelBookingModal
          open={isCancelModalOpen}
          onCancel={() => setIsCancelModalOpen(false)}
          bookings={[
            {
              id: data.bookingId,
              image: experience?.imageUrl,
              title: experience?.title,
              booking_id: data.reservationId,
              booked_on: data.created,
              uuid: data.uuid,
              client_name: client?.name,
              client_email: client?.email
            }
          ]}
          onConfirm={() => {
            setIsCancelModalOpen(false)
            refreshDetail()
          }}
        />
      )}
    </Flex>
  )
}

ReservationDetailPage.propTypes = {
  onBack: PropTypes.func
}

export default ReservationDetailPage
